"use client";
import NumberFlow from "@number-flow/react";
import Image from "next/image";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { forwardRef } from "react";
import { useRef } from "react";
import { useCallback, useState } from "react";
import { LuSmile } from "react-icons/lu";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEnterKeyTrigger } from "@/hooks/use-handle-keydown";
import { useReactionLogic } from "@/hooks/use-reaction-logic";
import { AssociationReactionValue } from "@/utils/constants";
import { cn } from "@/utils/lib";
import { ReactionEmojiAnimated } from "@/utils/reactions-utils";

type PostReactionSliderItemProps = {
  emoji: AssociationReactionValue;
  index: number;
  isReactionAlreadyDone: boolean;
  handleReaction: (value: {
    associationValue: AssociationReactionValue;
  }) => void;
  setHoveredEmoji: (emoji: string | null) => void;
  hoveredEmoji: string | null;
};

type TriggerProps = {
  hasAtLeastOneUserReaction: boolean;
  totalCount: number;
} & ComponentPropsWithoutRef<"div">;

const Trigger = forwardRef<HTMLDivElement, TriggerProps>(
  ({ hasAtLeastOneUserReaction, totalCount, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          `
            flex min-w-fit items-center gap-2 text-base text-muted outline-0
            hover:text-primary
          `,
          className,
          {
            "text-primary": hasAtLeastOneUserReaction,
          },
        )}
      >
        <LuSmile className="flex" />
        <div
          className={cn("text-xs", {
            hidden: totalCount === 0,
          })}
        >
          <NumberFlow
            value={totalCount}
            format={{
              notation: "compact",
            }}
          />
        </div>
      </div>
    );
  },
);
Trigger.displayName = "Trigger";

const PostReactionSliderItem = ({
  emoji,
  index,
  handleReaction,
  setHoveredEmoji,
  isReactionAlreadyDone,
  hoveredEmoji,
}: PostReactionSliderItemProps) => {
  const isGrayscale = hoveredEmoji !== null && emoji !== hoveredEmoji;

  const emojiStyle = cn(
    `
      inline-block animate-bump-fade-in cursor-pointer border px-1 text-3xl
      opacity-0 transition-all fill-mode-forwards
    `,
    {
      "scale-90! grayscale filter transition-all": isGrayscale,
      "rounded-full border-primary filter-none": isReactionAlreadyDone,
      "border-transparent": !isReactionAlreadyDone,
    },
  );

  const onClick = useCallback(() => {
    handleReaction({ associationValue: emoji });
  }, [emoji, handleReaction]);

  const handleKeyDown = useEnterKeyTrigger(onClick);

  return (
    <figure
      key={emoji}
      style={{ animationDelay: `${index * 0.05}s` }}
      className={emojiStyle}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHoveredEmoji(emoji)}
      onMouseLeave={() => setHoveredEmoji(null)}
    >
      <div
        className="
          origin-bottom text-center transition-all
          hover:scale-[1.2]
        "
      >
        <Image
          src={ReactionEmojiAnimated[emoji]}
          alt={emoji}
          width={32}
          height={32}
          unoptimized
        />
      </div>
    </figure>
  );
};

export const PostReactionSlider = ({
  customTrigger,
  postHash,
}: {
  customTrigger?: ReactNode;
  postHash: string;
}) => {
  const [hoveredEmoji, setHoveredEmoji] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const closeOnSelectionRef = useRef(false);

  const {
    reactions,
    totalCount,
    reactionsLoading,
    handleReaction,
    hasAtLeastOneUserReaction,
  } = useReactionLogic({
    postHash,
  });

  const trigger = customTrigger || (
    <Trigger
      hasAtLeastOneUserReaction={hasAtLeastOneUserReaction}
      totalCount={totalCount}
    />
  );

  return (
    <div>
      <Popover
        modal={modal}
        open={popupOpen}
        onOpenChange={(open) => {
          setPopupOpen(open);
          if (!open) {
            setModal(false);
            closeOnSelectionRef.current = false;
          }
        }}
      >
        <PopoverTrigger
          asChild
          openOnHover
          delay={0}
          closeDelay={250}
          onPointerDown={(ev) => {
            closeOnSelectionRef.current = ev.pointerType !== "mouse";
            setModal(ev.pointerType !== "mouse");
          }}
          className="flex items-center"
        >
          {trigger}
        </PopoverTrigger>
        <PopoverContent
          side="top"
          className="
            flex w-[330px] max-w-[100vw] items-center justify-center
            rounded-2xl bg-background p-4
          "
        >
          <div className="flex items-center justify-center">
            <div>
              {Object.values(AssociationReactionValue).map((emoji, index) => {
                const isReactionAlreadyDone = reactions[emoji].isReacted;

                return (
                  <PostReactionSliderItem
                    key={emoji}
                    {...{
                      hoveredEmoji,
                      setHoveredEmoji,
                      loading: reactionsLoading,
                      isReactionAlreadyDone,
                      handleReaction: (...args) => {
                        handleReaction(...args);
                        if (closeOnSelectionRef.current) {
                          setPopupOpen(false);
                        }
                      },
                      index,
                      emoji,
                    }}
                  />
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
